import {
  Box,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { ExternalLink, Globe, Plus, RotateCw, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { globalConfirm, globalSnackbar } from 'components/GlobalSnackbar';
import {
  SourceShell,
  Button as ShellButton,
  IconButton as ShellIconButton,
  EmptyState,
} from 'components/AppShell';
import { USE_NEW_SHELL } from 'core/flags';
import {
  addUrlSource,
  deleteUrlSource,
  listUrlSources,
  syncUrlSource,
  toggleUrlSourceEnabled,
} from '../api';
import type {
  SortDirection,
  UrlSourceListing,
  UrlSourceSortColumn,
  UrlSourcesListResponse,
} from '../api/types';
import AddUrlSourceModal from '../components/AddUrlSourceModal';
import LegacyUrlSourcesPage from './UrlSourcesPage.legacy';

interface RootState {
  reducer: {
    project: { id: string };
  };
}

const PAGE_SIZE = 12;

type EnabledFilter = 'all' | 'on' | 'off';
type SortPreset = 'created_desc' | 'created_asc' | 'updated_desc' | 'title_asc';

const SORT_PRESETS: Record<
  SortPreset,
  { column: UrlSourceSortColumn; direction: SortDirection; label: string }
> = {
  created_desc: { column: 'created_at', direction: 'DESC', label: 'Сначала новые' },
  created_asc: { column: 'created_at', direction: 'ASC', label: 'Сначала старые' },
  updated_desc: { column: 'updated_at', direction: 'DESC', label: 'По дате обновления' },
  title_asc: { column: 'title', direction: 'ASC', label: 'По алфавиту' },
};

interface Stats {
  total: number;
  enabled: number;
}

const STATS_QUERY = 'url-sources-stats';
const LIST_QUERY = 'url-sources-list';

const formatUpdatedAt = (unixSeconds: number): string => {
  if (!unixSeconds) return '';
  const diffSec = Math.max(0, Math.floor(Date.now() / 1000) - unixSeconds);
  if (diffSec < 60) return 'только что';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} мин назад`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} ч назад`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)} дн назад`;
  try {
    return new Date(unixSeconds * 1000).toLocaleDateString('ru', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const extractHostname = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const extractMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const Favicon = ({ url, size = 18 }: { url: string; size?: number }) => {
  const [errored, setErrored] = useState(false);
  const host = useMemo(() => {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  }, [url]);

  if (errored || !host) {
    return <Globe size={size} strokeWidth={1.75} color="var(--shell-text-tertiary)" />;
  }
  return (
    <Box
      component="img"
      src={`https://favicon.yandex.net/favicon/${host}`}
      alt=""
      onError={() => setErrored(true)}
      sx={{
        width: size,
        height: size,
        borderRadius: 0.5,
        objectFit: 'contain',
        flexShrink: 0,
      }}
    />
  );
};

const UrlSourcesPageNew = () => {
  const currentProject = useSelector((state: RootState) => state.reducer.project);
  const projectId = currentProject?.id ?? '';
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>('all');
  const [sortPreset, setSortPreset] = useState<SortPreset>('created_desc');
  const [addModalOpen, setAddModalOpen] = useState(false);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Дебаунс поиска ────────────────────────────────────────────────────────
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [projectId, enabledFilter, sortPreset]);

  // ─── Список источников ─────────────────────────────────────────────────────
  const sort = SORT_PRESETS[sortPreset];
  const listKey = useMemo(
    () => [LIST_QUERY, projectId, page, appliedSearch, enabledFilter, sortPreset] as const,
    [projectId, page, appliedSearch, enabledFilter, sortPreset]
  );

  const listQuery = useQuery({
    queryKey: listKey,
    enabled: Boolean(projectId),
    staleTime: 30_000,
    queryFn: () =>
      listUrlSources({
        project_id: projectId,
        name: appliedSearch || undefined,
        is_enabled: enabledFilter === 'all' ? undefined : enabledFilter === 'on',
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        sort_column: sort.column,
        sort_direction: sort.direction,
      }),
  });

  const items: UrlSourceListing[] = listQuery.data?.items ?? [];
  const total = listQuery.data?.count ?? 0;
  const loading = listQuery.isLoading || listQuery.isFetching;

  // ─── Статистика (total + enabled), один раз на проект ──────────────────────
  const statsQuery = useQuery({
    queryKey: [STATS_QUERY, projectId],
    enabled: Boolean(projectId),
    staleTime: Infinity,
    queryFn: async (): Promise<Stats> => {
      const [allRes, onRes] = await Promise.all([
        listUrlSources({ project_id: projectId, limit: 1 }),
        listUrlSources({ project_id: projectId, is_enabled: true, limit: 1 }),
      ]);
      return { total: allRes.count, enabled: onRes.count };
    },
  });

  const stats = statsQuery.data ?? { total: 0, enabled: 0 };
  const disabledCount = Math.max(0, stats.total - stats.enabled);

  const patchStats = useCallback(
    (delta: Partial<Stats>) => {
      queryClient.setQueryData<Stats>([STATS_QUERY, projectId], (prev) => {
        const base = prev ?? { total: 0, enabled: 0 };
        return {
          total: Math.max(0, base.total + (delta.total ?? 0)),
          enabled: Math.max(0, base.enabled + (delta.enabled ?? 0)),
        };
      });
    },
    [queryClient, projectId]
  );

  const patchListItem = useCallback(
    (id: string, updater: (item: UrlSourceListing) => UrlSourceListing) => {
      queryClient.setQueryData<UrlSourcesListResponse>(listKey, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) => (item.id === id ? updater(item) : item)),
        };
      });
    },
    [queryClient, listKey]
  );

  // ─── Мутации ───────────────────────────────────────────────────────────────
  const addMutation = useMutation({
    mutationFn: (vars: { url: string; cleanMode: boolean }) =>
      addUrlSource({
        project_id: projectId,
        url: vars.url,
        clean_mode: vars.cleanMode,
      }),
    onSuccess: (created) => {
      if (!created) return;
      globalSnackbar({ message: 'Источник добавлен', severity: 'success' });
      setAddModalOpen(false);
      setPage(1);
      patchStats({ total: 1, enabled: created.is_enabled ? 1 : 0 });
      queryClient.invalidateQueries({ queryKey: [LIST_QUERY, projectId] });
    },
    onError: (error) => {
      globalSnackbar({
        message: `Ошибка добавления: ${extractMessage(error, 'неизвестная ошибка')}`,
        severity: 'error',
      });
    },
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => syncUrlSource(id),
    onSuccess: (updated, id) => {
      if (!updated) return;
      patchListItem(id, () => ({
        id: updated.id,
        url: updated.url,
        title: updated.title,
        clean_mode: updated.clean_mode,
        is_enabled: updated.is_enabled,
        created_at: updated.created_at,
        updated_at: updated.updated_at,
      }));
      globalSnackbar({ message: 'Источник обновлён', severity: 'success' });
    },
    onError: (error) => {
      const err = error as { status?: number };
      if (err.status === 404) {
        globalSnackbar({ message: 'Источник не найден — список обновлён', severity: 'warning' });
        queryClient.invalidateQueries({ queryKey: [LIST_QUERY, projectId] });
        queryClient.invalidateQueries({ queryKey: [STATS_QUERY, projectId] });
      } else {
        globalSnackbar({
          message: `Ошибка обновления: ${extractMessage(error, 'неизвестная ошибка')}`,
          severity: 'error',
        });
      }
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (vars: { source: UrlSourceListing; value: boolean }) =>
      toggleUrlSourceEnabled(vars.source.id, vars.value),
    onMutate: ({ source, value }) => {
      patchListItem(source.id, (item) => ({ ...item, is_enabled: value }));
      patchStats({ enabled: value ? 1 : -1 });
      return { source, value };
    },
    onError: (error, _vars, context) => {
      if (context) {
        patchListItem(context.source.id, (item) => ({ ...item, is_enabled: !context.value }));
        patchStats({ enabled: context.value ? -1 : 1 });
      }
      const err = error as { status?: number };
      if (err.status === 404) {
        globalSnackbar({ message: 'Источник не найден — список обновлён', severity: 'warning' });
        queryClient.invalidateQueries({ queryKey: [LIST_QUERY, projectId] });
        queryClient.invalidateQueries({ queryKey: [STATS_QUERY, projectId] });
      } else {
        globalSnackbar({
          message: `Не удалось изменить состояние: ${extractMessage(error, 'неизвестная ошибка')}`,
          severity: 'error',
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (source: UrlSourceListing) => deleteUrlSource(source.id),
    onSuccess: (_data, source) => {
      globalSnackbar({ message: 'Источник удалён', severity: 'success' });
      patchStats({ total: -1, enabled: source.is_enabled ? -1 : 0 });
      if (items.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        queryClient.invalidateQueries({ queryKey: [LIST_QUERY, projectId] });
      }
    },
    onError: (error) => {
      const err = error as { status?: number };
      if (err.status === 404) {
        globalSnackbar({ message: 'Источник уже удалён — список обновлён', severity: 'warning' });
        queryClient.invalidateQueries({ queryKey: [LIST_QUERY, projectId] });
        queryClient.invalidateQueries({ queryKey: [STATS_QUERY, projectId] });
      } else {
        globalSnackbar({
          message: `Ошибка удаления: ${extractMessage(error, 'неизвестная ошибка')}`,
          severity: 'error',
        });
      }
    },
  });

  const handleAdd = useCallback(
    async (url: string, cleanMode: boolean) => {
      await addMutation.mutateAsync({ url, cleanMode });
    },
    [addMutation]
  );

  const handleDelete = useCallback(
    (source: UrlSourceListing) => {
      globalConfirm({
        title: 'Удалить источник',
        message: `Удалить «${source.title || source.url}»?`,
        confirmText: 'Удалить',
        onCancel: () => undefined,
        onConfirm: () => deleteMutation.mutate(source),
      });
    },
    [deleteMutation]
  );

  const syncingIds = useMemo(
    () => new Set(syncMutation.variables ? [syncMutation.variables] : []),
    [syncMutation.variables]
  );

  const pageCount = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const hasFilters = appliedSearch || enabledFilter !== 'all';

  const toolbar = projectId ? (
    <>
      <TextField
        size="small"
        placeholder="Поиск по названию…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{
          flex: 1,
          minWidth: 220,
          maxWidth: 360,
          '& .MuiOutlinedInput-root': {
            height: 32,
            fontSize: 13,
            background: 'var(--shell-bg-surface)',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={14} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
            </InputAdornment>
          ),
        }}
      />
      <ToggleButtonGroup
        size="small"
        exclusive
        value={enabledFilter}
        onChange={(_, v) => v && setEnabledFilter(v as EnabledFilter)}
        sx={{
          height: 32,
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            px: 1.5,
            fontSize: 12,
            color: 'var(--shell-text-secondary)',
            borderColor: 'var(--shell-border-default)',
            '&.Mui-selected': {
              background: 'var(--shell-accent-subtle)',
              color: 'var(--shell-accent-pressed)',
              borderColor: 'var(--shell-accent-primary)',
            },
          },
        }}
      >
        <ToggleButton value="all">Все</ToggleButton>
        <ToggleButton value="on">Включённые</ToggleButton>
        <ToggleButton value="off">Выключенные</ToggleButton>
      </ToggleButtonGroup>
      <Select
        size="small"
        value={sortPreset}
        onChange={(e) => setSortPreset(e.target.value as SortPreset)}
        MenuProps={{
          PaperProps: { sx: { mt: 0.5 } },
          sx: { zIndex: 1500 },
        }}
        sx={{
          minWidth: 200,
          height: 32,
          fontSize: 13,
          background: 'var(--shell-bg-surface)',
        }}
      >
        {(Object.keys(SORT_PRESETS) as SortPreset[]).map((key) => (
          <MenuItem key={key} value={key}>
            {SORT_PRESETS[key].label}
          </MenuItem>
        ))}
      </Select>
    </>
  ) : null;

  const subtitle = projectId ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      <span>Сохраняйте веб-страницы как контекст для ассистента</span>
      <span style={{ color: 'var(--shell-border-default)' }}>·</span>
      <span style={{ color: 'var(--shell-text-secondary)' }}>
        Всего: <strong style={{ color: 'var(--shell-text-primary)' }}>{stats.total}</strong>
      </span>
      <span style={{ color: 'var(--shell-accent-pressed)' }}>
        Включено: <strong>{stats.enabled}</strong>
      </span>
      <span style={{ color: 'var(--shell-text-tertiary)' }}>Выключено: {disabledCount}</span>
    </span>
  ) : (
    'Сохраняйте веб-страницы как контекст для ассистента'
  );

  return (
    <>
      <SourceShell
        title="URL-источники"
        subtitle={subtitle}
        toolbar={toolbar}
        actions={
          <ShellButton
            variant="primary"
            leftIcon={<Plus size={14} strokeWidth={2} />}
            onClick={() => setAddModalOpen(true)}
            disabled={!projectId}
          >
            Добавить URL
          </ShellButton>
        }
      >
        {/* ───────────────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
          {!projectId && (
            <EmptyState
              title="Выберите проект"
              description="URL-источники привязаны к проекту. Выберите проект в шапке."
            />
          )}

          {projectId && loading && items.length === 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 1.5,
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1.5,
                    border: '1px solid var(--shell-border-subtle)',
                    borderRadius: 'var(--shell-radius-lg)',
                    background: 'var(--shell-bg-surface)',
                  }}
                >
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              ))}
            </Box>
          )}

          {projectId && !loading && items.length === 0 && (
            <EmptyState
              title={hasFilters ? 'Ничего не найдено' : 'Пока нет источников'}
              description={
                hasFilters
                  ? 'Попробуйте сбросить фильтры или изменить поисковый запрос.'
                  : 'Добавьте первый URL — бэк скачает страницу и сохранит её содержимое.'
              }
              action={
                hasFilters ? (
                  <ShellButton
                    variant="secondary"
                    onClick={() => {
                      setSearchInput('');
                      setAppliedSearch('');
                      setEnabledFilter('all');
                    }}
                  >
                    Сбросить фильтры
                  </ShellButton>
                ) : (
                  <ShellButton
                    variant="primary"
                    leftIcon={<Plus size={14} strokeWidth={2} />}
                    onClick={() => setAddModalOpen(true)}
                  >
                    Добавить первый URL
                  </ShellButton>
                )
              }
            />
          )}

          {items.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 1.5,
                opacity: loading ? 0.6 : 1,
                transition: 'opacity 0.15s ease',
              }}
            >
              {items.map((source) => {
                const isSyncing = syncMutation.isPending && syncMutation.variables === source.id;
                const isToggling =
                  toggleMutation.isPending && toggleMutation.variables?.source.id === source.id;
                const hostname = extractHostname(source.url);
                return (
                  <Box
                    key={source.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 'var(--shell-radius-lg)',
                      border: '1px solid var(--shell-border-subtle)',
                      background: 'var(--shell-bg-surface)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      minHeight: 140,
                      position: 'relative',
                      transition: 'border-color 120ms ease, background 120ms ease',
                      opacity: source.is_enabled ? 1 : 0.7,
                      '&:hover': {
                        borderColor: 'var(--shell-border-default)',
                        background: 'var(--shell-bg-canvas)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 12,
                        bottom: 12,
                        width: 2,
                        borderRadius: '0 2px 2px 0',
                        background: source.is_enabled
                          ? 'var(--shell-accent-primary)'
                          : 'var(--shell-border-default)',
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          background: 'var(--shell-bg-subtle)',
                          border: '1px solid var(--shell-border-subtle)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Favicon url={source.url} size={16} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Tooltip title={source.title || source.url} placement="top">
                          <Box
                            component="a"
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'var(--shell-text-primary)',
                              textDecoration: 'none',
                              fontWeight: 600,
                              fontSize: 13.5,
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              '&:hover': { color: 'var(--shell-accent-primary)' },
                            }}
                          >
                            {source.title || hostname}
                          </Box>
                        </Tooltip>
                        <Box
                          sx={{
                            fontSize: 11,
                            color: 'var(--shell-text-tertiary)',
                            mt: 0.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={source.url}
                        >
                          {hostname}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 7px',
                          borderRadius: 999,
                          background: source.clean_mode
                            ? 'var(--shell-accent-subtle)'
                            : 'var(--shell-bg-subtle)',
                          color: source.clean_mode
                            ? 'var(--shell-accent-pressed)'
                            : 'var(--shell-text-tertiary)',
                          border: `1px solid ${source.clean_mode ? 'var(--shell-accent-primary)' : 'var(--shell-border-subtle)'}`,
                        }}
                      >
                        {source.clean_mode ? 'Только текст' : 'HTML целиком'}
                      </span>
                      {source.updated_at ? (
                        <Tooltip
                          title={`Обновлён: ${new Date(source.updated_at * 1000).toLocaleString(
                            'ru'
                          )}`}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              padding: '2px 7px',
                              borderRadius: 999,
                              background: 'var(--shell-bg-subtle)',
                              color: 'var(--shell-text-tertiary)',
                            }}
                          >
                            {formatUpdatedAt(source.updated_at)}
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>

                    <Box sx={{ flex: 1 }} />

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pt: 1,
                        mt: 'auto',
                        borderTop: '1px solid var(--shell-border-subtle)',
                      }}
                    >
                      <Tooltip title={source.is_enabled ? 'Включён в контекст' : 'Выключен'}>
                        <Switch
                          size="small"
                          checked={source.is_enabled}
                          disabled={isToggling}
                          onChange={(e) =>
                            toggleMutation.mutate({ source, value: e.target.checked })
                          }
                        />
                      </Tooltip>
                      <Box sx={{ display: 'flex', gap: 0 }}>
                        <ShellIconButton
                          size="sm"
                          label="Открыть в новой вкладке"
                          onClick={() => window.open(source.url, '_blank', 'noopener')}
                          icon={<ExternalLink size={14} strokeWidth={1.75} />}
                        />
                        <ShellIconButton
                          size="sm"
                          label="Перечитать страницу"
                          onClick={() => syncMutation.mutate(source.id)}
                          disabled={isSyncing || syncingIds.has(source.id)}
                          icon={
                            isSyncing ? (
                              <CircularProgress
                                size={14}
                                sx={{ color: 'var(--shell-accent-primary)' }}
                              />
                            ) : (
                              <RotateCw size={14} strokeWidth={1.75} />
                            )
                          }
                        />
                        <ShellIconButton
                          size="sm"
                          tone="danger"
                          label="Удалить"
                          onClick={() => handleDelete(source)}
                          icon={<Trash2 size={14} strokeWidth={1.75} />}
                        />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {pageCount > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 1,
              flexShrink: 0,
              borderTop: '1px solid var(--shell-border-subtle)',
            }}
          >
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, value) => setPage(value)}
              size="small"
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </SourceShell>

      <AddUrlSourceModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={handleAdd}
      />
    </>
  );
};

const UrlSourcesPage = () => {
  if (!USE_NEW_SHELL) {
    return <LegacyUrlSourcesPage />;
  }
  return <UrlSourcesPageNew />;
};

export default UrlSourcesPage;
